<template>
    <v-form class="pt-4" :disabled="!availableToEdit">
        <v-radio-group v-if="companyType === Company.Corporator" v-model="formData.useDirectSalesSettings">
            <v-radio :label="$t('use_direct_sales_settings')" :value="true" data-cy="use-direct-salesSettings" />
            <v-radio
                :label="$t('not_use_direct_sales_settings')"
                :value="false"
                data-cy="not-use-direct-salesSettings"
            />
        </v-radio-group>
        <template
            v-if="
                !companyType ||
                (companyType === Company.Corporator && !formData.useDirectSalesSettings) ||
                companyType !== Company.Corporator
            "
        >
            <v-row v-if="companyType" no-gutters class="pb-4">
                <v-col cols="12" md="6" sm="6">
                    <v-row no-gutters class="align-center justify-space-between gc-4">
                        <div class="text-h6">{{ $t('contract') }}</div>
                        <CopyContractModal
                            v-if="salesSettings?.secondLevelInfoTabs && availableToEdit"
                            ref="copyContractRef"
                            v-model="formData.contractGroup"
                            :available-tabs="salesSettings?.secondLevelInfoTabs"
                            available-clients-contracts
                            @copied="copyCompanyContract"
                        />
                    </v-row>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                        v-model="contractGroupId"
                        :items="contractGroups"
                        :label="$t('group_of_contracts')"
                        item-value="id"
                        item-title="name"
                        clearable
                        data-cy="contract-group"
                        hide-details="auto"
                        class="mb-4"
                        @update:model-value="getGroupOfContracts"
                    />
                    <template v-if="companyType">
                        <v-text-field
                            v-model="formData.contractNumber"
                            :label="`${$t('contract')} #`"
                            data-cy="contract-group"
                            hide-details="auto"
                            class="mb-4"
                        />
                        <div class="mb-4">
                            <date-picker
                                v-model="formData.validTill"
                                color="primary"
                                outlined
                                :label="$t('valid_till')"
                                data-cy="contract-group"
                                hide-details
                            />
                        </div>
                        <v-text-field
                            :model-value="authStore.company.name"
                            disabled
                            :label="$t('supplier_id')"
                            hide-details="auto"
                            class="mb-4"
                        />
                    </template>
                </v-col>
            </v-row>
            <v-row no-gutters class="align-center ga-2 pb-4">
                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete
                        v-model="formData.managerId"
                        item-title="name"
                        item-value="personId"
                        :items="managers"
                        :label="$t('manager')"
                        :loading="isManagersLoading"
                        :disabled="isManagersLoading"
                        data-cy="manager-id"
                        hide-details
                    />
                </v-col>
                <v-btn
                    size="small"
                    icon="mdi-information-outline"
                    variant="text"
                    data-cy="is-show-info-modal"
                    @click="isShowInfoModal = true"
                />
            </v-row>
        </template>
        <ManagerInfoModal v-model="isShowInfoModal" :manager="useManagerInfo" />
        <v-form
            v-if="
                formData.contractGroup &&
                (companyType !== Company.Corporator ||
                    (companyType === Company.Corporator && !formData.useDirectSalesSettings))
            "
            :disabled="isReadOnly || !availableToEdit"
        >
            <SalesSettingsForm ref="salesSettings" v-model="formData.contractGroup" />
        </v-form>
        <v-row class="pa-4 pt-10 gc-4">
            <v-btn v-if="availableToEdit" :disabled="isLoading" :loading="isSaving" @click="saveCompanyContract">
                {{ $t('save') }}
            </v-btn>
            <slot name="actions" />
        </v-row>
    </v-form>
    <PropagateManagerToOldOrdersModal v-model="isShowModal" @on-save="saveUpdatedContract" />
</template>

<script setup lang="ts">
    import {
        companyContractGetToPutTransformer,
        contractGroupGetToPutTransformer,
        contractGroupSaveTransformer,
    } from '~/adapters/salesSettingsAdapter'
    import {Company} from '~/utils/types/types'
    import DatePicker from '~/components/snippets/forms/DatePicker.vue'
    import {format} from 'date-fns'

    const props = withDefaults(
        defineProps<{
            companyId?: number
            companyType?: string
            b2cId?: number
            isShowInvoices?: boolean
            isShowAdditionally?: boolean
            contractGroupPlace?: string
        }>(),
        {
            companyId: null,
            companyType: null,
            b2cId: null,
            isShowInvoices: false,
            isShowAdditionally: false,
            contractGroupPlace: 'CONTRACT_GROUP',
        }
    )

    const {contractGroups} = useContractGroups()
    const isLoading = ref<boolean>(false)
    const isSaving = ref<boolean>(false)
    const formData = ref({})
    const defaultFormData = ref({})
    const managers = ref([])
    const isManagersLoading = ref<boolean>(false)
    const isShowInfoModal = ref<boolean>(false)
    const contractGroupId = ref<number>(null)
    const availableToEdit = ref<boolean>(!authStore.isAgency)
    const isShowModal = ref<boolean>(false)
    const salesSettings = ref(null)
    const copyContractRef = ref(null)

    const isReadOnly = ref<boolean>(false)
    provide('isReadOnly', isReadOnly)
    const isShowCalculationCommission = ref<boolean>(props.companyType && props.companyType !== Company.Corporator)
    provide('isShowCalculationCommission', isShowCalculationCommission)
    const isTAContract = ref<boolean>(props.companyType && props.companyType !== Company.Corporator)
    provide('isTAContract', isTAContract.value)
    const isCorpContract = ref<boolean>(props.companyType && props.companyType === Company.Corporator)
    provide('isCorpContract', isCorpContract.value)
    provide('isShowInvoices', props.isShowInvoices)
    provide('isShowAdditionally', props.isShowAdditionally)
    provide('contractGroupPlace', props.contractGroupPlace)

    const {$api, $toast} = useNuxtApp()
    const {t} = useI18n()

    onMounted(async () => {
        if (props.companyId || props.b2cId) await loadData()
        await loadManagers()
    })

    async function loadData(): Promise<void> {
        isLoading.value = true
        let query
        if (props.companyId) query = {companyId: props.companyId}
        if (props.b2cId) query = {b2cId: props.b2cId}

        try {
            const resp = await $api.companyContract.get(query)
            formData.value = companyContractGetToPutTransformer(clone(resp))

            formData.value.companyId = props.companyId || props.b2cId
            isReadOnly.value = resp.contractGroup.contractGroupType === 'CONTRACT_GROUP'
            if (formData.value.validTill) {
                formData.value.validTill = format(new Date(formData.value.validTill), 'yyyy-MM-dd')
            }
            if (contractGroups.value.some(({id}) => id === formData.value.contractGroup.id)) {
                contractGroupId.value = formData.value.contractGroup.id
            }

            defaultFormData.value = clone(formData.value)
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isLoading.value = false
        }
    }

    async function loadManagers(): Promise<void> {
        isManagersLoading.value = true
        try {
            managers.value = (await $api.persons.get({companyId: authStore.company.id})).users
                .map(manager => ({
                    ...manager,
                    name: `${manager.lastName || ''} ${manager.firstName || ''}`.trim(),
                }))
                .sort((a, b) => a.name.localeCompare(b.name))
        } catch (e) {
            $toast.error(t('error_message.load_error'))
        } finally {
            isManagersLoading.value = false
        }
    }

    const getGroupOfContracts = async () => {
        if (contractGroupId.value) {
            isReadOnly.value = true
            isLoading.value = true
            try {
                const contractGroup = await $api.contractGroup.get({
                    contractGroupId: contractGroupId.value,
                    contractGroupPlace: props.contractGroupPlace,
                })
                formData.value.contractGroup = await contractGroupGetToPutTransformer(contractGroup)
            } catch (e) {
                isReadOnly.value = false
                $toast.error(t('error_message.load_error'))
            } finally {
                isLoading.value = false
            }
        } else {
            isReadOnly.value = false
            await resetContract()
        }
        formData.value.contractGroup.id = contractGroupId.value

        await nextTick()
        EventBus.$emit('UPDATE_CONTRACT')
    }

    const resetContract = async () => {
        formData.value = clone(defaultFormData.value)
    }

    const saveCompanyContract = async () => {
        if (formData.value.managerId !== defaultFormData.value.managerId) {
            isShowModal.value = true
        } else {
            await saveContract()
        }
    }

    const saveUpdatedContract = async isPropagate => {
        formData.value.propagateManagerToOldOrders = isPropagate
        await saveContract()
    }

    const saveContract = async () => {
        const contract = clone(formData.value)
        contract.contractGroup = await contractGroupSaveTransformer(contract.contractGroup)

        try {
            isSaving.value = true
            await $api.companyContract.put(contract)
            $toast.success(t('success_message.saved_successfully'))
            defaultFormData.value = clone(contract)
            delete formData.value.propagateManagerToOldOrders
        } catch (e) {
            $toast.error(e.msg || e.errors[0].message || t('error_message.save_error'))
        } finally {
            isSaving.value = false
        }
    }

    const copyCompanyContract = async () => {
        contractGroupId.value = null
        delete formData.value.contractGroup.id
        if (formData.value.managerId) {
            formData.value.managerId = defaultFormData.value.managerId
        }
        await saveContract()
        isReadOnly.value = false
    }

    const useManagerInfo = computed(() => {
        return managers.value.find(({personId}) => personId === formData.value?.managerId)
    })
</script>
